import {default as cx} from "classnames"
import {Calendar} from "primereact/calendar"
import {Dropdown} from "primereact/dropdown"
import {Skeleton} from "primereact/skeleton"
import {useEffect, useState} from "react"
import {useParams, useSearchParams} from "react-router-dom"

import {ORIENTATION_STATUS, getLocaleDate, formatDate, normalizeDate, capitalizePersonName} from "../../utilities/general"
import SubHeader from "../SubHeader"
import {RadioButtonAnswer} from "../prompts/shared/RadioButtonAnswer"
import {useStableNavigate} from "../../utilities/StableNavigateContext"
import {wrappedPost, wrappedGet} from "../../utilities/AxiosHelper"

const initialOrientationStatusOptions = Object.entries(ORIENTATION_STATUS).map(([key, id]) => ({
    id,
    label: key
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase()),
})).filter((f) => f.id !== ORIENTATION_STATUS.CANCELLED && f.id !== ORIENTATION_STATUS.ONBOARDING)

const getWeeksToAddPreview = (suspensionStartDate) => {
    if (!suspensionStartDate) return 0

    const start = new Date(suspensionStartDate)
    const today = new Date()

    // compare pure dates (strip time), matching the backend's .date() comparison
    const startDateOnly = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate())
    const todayDateOnly = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())

    const totalSuspendedDays = (todayDateOnly - startDateOnly) / (1000 * 60 * 60 * 24)
    return Math.round(totalSuspendedDays / 7)
}

const EducatorOrienteeStatus = () => {
    const navigate = useStableNavigate()
    const {orientation_id} = useParams()
    const [searchParams] = useSearchParams()
    const [isLoading, setIsLoading] = useState(true)
    const [isUnitLoading, setIsUnitLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState(null)
    const [orientation, setOrientation] = useState({})
    const [reasons, setReasons] = useState([])
    const [facilityId, setFacilityId] = useState(null)
    const [facilities, setFacilities] = useState([])
    const [unitId, setUnitId] = useState(null)
    const [units, setUnits] = useState([])
    const [ineligibleReasons, setIneligibleReasons] = useState([])
    const [continueOrientation, setContinueOrientation] = useState("current")
    const [selectedStatusId, setSelectedStatusId] = useState(null)
    const [selectedReasonId, setSelectedReasonId] = useState(null)
    const [ineligibleReasonId, setIneligibleReasonId] = useState(null)
    const [preceptedShiftsInFinalWeek, setPreceptedShiftsInFinalWeek] = useState(0)
    const [orientationEndDate, setOrientationEndDate] = useState(normalizeDate(new Date()))
    const [makePreceptor, setMakePreceptor] = useState(false)
    const [orientationStatusOptions, setOrientationStatusOptions] = useState(initialOrientationStatusOptions)

    const saveDisabled =
        saving ||
        selectedStatusId === orientation.orientation_status_id ||
        (selectedStatusId === ORIENTATION_STATUS.ATTRITIONED && (!selectedReasonId || !orientationEndDate)) ||
        (selectedStatusId === ORIENTATION_STATUS.COMPLETED &&
            (preceptedShiftsInFinalWeek === null || preceptedShiftsInFinalWeek === undefined || !orientationEndDate)) ||
        (selectedStatusId === ORIENTATION_STATUS.TRANSFER && unitId === orientation?.unit_id) ||
        (selectedStatusId === ORIENTATION_STATUS.INELIGIBLE && !ineligibleReasonId)

    const isResumingFromPause =
        orientation.orientation_status_id === ORIENTATION_STATUS.PAUSED &&
        selectedStatusId === ORIENTATION_STATUS.ACTIVE
    const hasChange = selectedStatusId !== orientation.orientation_status_id

    useEffect(() => {
        loadComponentData()
    }, [])

    const loadComponentData = async () => {
        try {

            // if the user is coming from the onboarding wizard limit the options to Ineligible, Cancelled
            if (searchParams.get("returnTo") === "onboarding") {
                const filteredStatusOptions = Object.entries(ORIENTATION_STATUS).map(([key, id]) => {
                    if (key === 'INELIGIBLE' || key === 'CANCELLED') {
                        return {
                            id,
                            label: key
                                .toLowerCase()
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (char) => char.toUpperCase()),
                        }
                    }
                }).filter(item => item !== null && item !== undefined)
                setOrientationStatusOptions(filteredStatusOptions)
            }

            setIsLoading(true)
            const [response, responseReasons, responseIneligibleReasons] = await Promise.all([
                wrappedGet(`/unit-orientation/${orientation_id}`),
                wrappedGet(`/orientees/${orientation_id}/orientation-unsuccessful-reasons`),
                wrappedGet(`/orientees/${orientation_id}/orientation-ineligible-reasons`),
            ])

            setOrientation(response.data)
            setSelectedStatusId(response.data.orientation_status_id)
            setReasons(responseReasons.data.results)
            setIneligibleReasons(responseIneligibleReasons.data.results)

            if (response?.data?.organization_id) {
                const facilityResponse = await wrappedGet(`/organization/${response.data?.organization_id}/facility`)
                setFacilities(facilityResponse.data.results)
            }

            if (response?.data?.facility_id) {
                setFacilityId(response?.data?.facility_id)
                const unitResponse = await wrappedGet(
                    `/organization/${response.data?.organization_id}/facility/${response.data.facility_id}/unit`
                )
                setUnits(unitResponse.data.results)
            }

            if (response?.data?.unit_id) {
                setUnitId(response?.data?.unit_id)
            }
        } catch (error) {
            setError("Failed to load orientation data")
        } finally {
            setIsLoading(false)
        }
    }

    const fetchUnits = async (facility_id) => {
        setIsUnitLoading(true)
        const response = await wrappedGet(`/organization/${orientation?.organization_id}/facility/${facility_id}/unit`)
        setUnits(response.data.results)
        setIsUnitLoading(false)
    }

    const handleSave = () => {
        setSaving(true)
        setError(null)
        wrappedPost(`/orientees/${orientation.id}/status`, {
            // do not change the status when transferring
            orientation_status_id: selectedStatusId,
            ...(selectedStatusId === ORIENTATION_STATUS.ATTRITIONED && {
                orientation_end_date: orientationEndDate,
                orientation_unsuccessful_reason_id: selectedReasonId,
            }),
            ...(selectedStatusId === ORIENTATION_STATUS.COMPLETED && {
                make_preceptor: makePreceptor,
                orientation_end_date: orientationEndDate,
                precepted_shifts_in_final_week: preceptedShiftsInFinalWeek,
            }),
            ...(selectedStatusId === ORIENTATION_STATUS.TRANSFER && {
                change_reason: "transfer",
                facility_id: facilityId,
                unit_id: unitId,
                continue_orientation: continueOrientation,
            }),
            ...(selectedStatusId === ORIENTATION_STATUS.INELIGIBLE && {
                orientation_ineligible_reason_id: ineligibleReasonId,
            }),
        })
            .then(() => {
                setSaving(false)
                navigate(`/educator-orientations`)
            })
            .catch((error) => {
                setError(error.response?.data?.Error ?? "An error occurred while saving the orientee status")
                setSaving(false)
                console.error(error)
            })
    }

    if (isLoading) {
        return (
            <div className="card p-8 mt-6">
                <Skeleton width="100%" height="10rem" />
            </div>
        )
    }

    return (
        <>
            <SubHeader title1="" title2="Manage Orientation Status">
                <div className="d-flex flex-row flex-grow-1 justify-content-end align-items-center gap-3">
                    {searchParams && searchParams.get("returnTo") === "onboarding" ? (
                        <button
                            className="btn btn-light-primary btn-sm ms-2"
                            onClick={() => {
                                navigate("/educator-onboarding")
                            }}
                        >
                            Return to Onboard Orientees
                        </button>
                    ) : (
                        <button
                            className="btn btn-light-primary btn-sm ms-2"
                            onClick={() => {
                                navigate(`/educator-orientations/${orientation_id}`)
                            }}
                        >
                            {`Return to ${orientation.name}'s Details `}
                        </button>
                    )}
                </div>
            </SubHeader>
            <div className="container d-flex flex-column">
                <div className="card mt-4 border border-gray-400">
                    <div className="px-4 py-4 gap-3 gap-sm-0">
                        <div className="d-flex flex-row h3 pt-5">{orientation.name}</div>
                        <div className="d-grid d-sm-flex flex-row flex-wrap fw-semibold fs-6 gap-3 gap-sm-2 pt-4 pb-6 py-sm-4">
                            <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                                <i className="fas fa-hospital text-primary fs-6 me-2"></i>
                                {orientation.unit_name}
                            </span>
                            <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                                <i className="fas fa-user-nurse text-primary fs-6 me-2"></i>
                                {orientation.position_name}
                            </span>
                            <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                                <i className="fas fa-clock-rotate-left text-primary fs-6 me-2"></i>
                                Prior Exp: {orientation.experience_level_name}
                            </span>
                            <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                                <i className="fas fa-graduation-cap text-primary fs-6 me-2"></i>
                                {orientation.nursing_program_completed}
                                {orientation.nurse_graduate_board_date &&
                                    `, ${formatDate(orientation.nurse_graduate_board_date)}`}
                            </span>
                            <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                                <i className="fas fa-flag text-primary fs-6 me-2"></i>
                                Hired: {formatDate(orientation.hire_date)}
                            </span>
                            <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                                <i className="fas fa-flag text-primary fs-6 me-2"></i>
                                Started: {formatDate(orientation.start_date)}
                            </span>
                            <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                                <i className="fas fa-calendar-xmark text-primary fs-6 me-2"></i>
                                Expected End Date: {getLocaleDate(orientation.expected_end_date)}
                            </span>
                            <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                                <i className="fas fa-calendar-week text-primary fs-6 me-2"></i>
                                Length: {orientation.standard_orientation_length_weeks}w
                            </span>
                            <span className="d-flex align-items-start align-items-sm-center me-0 me-sm-5 mb-0 mb-sm-2">
                                <i className="fas fa-hospital-user text-primary fs-6 me-2"></i>
                                Preceptors: {(orientation.preceptors ?? []).map((p) => capitalizePersonName(p.preceptor_name)).join(", ")}
                            </span>
                        </div>

                        <div className="h3 my-5">Orientation Status: Last Updated {formatDate(orientation.updated_at)}</div>
                        <div className="row">
                            <div className="col-12 col-md-6 mb-8">
                                <label className="form-label required">Current Status</label>
                                <RadioButtonAnswer
                                    field="orientation-status"
                                    options={(orientationStatusOptions ?? []).map((option) => ({
                                        label: option.label,
                                        value: option.id,
                                    }))}
                                    data={selectedStatusId}
                                    handle={(value) => setSelectedStatusId(Number(value))}
                                />
                            </div>
                        </div>

                        {selectedStatusId === ORIENTATION_STATUS.ATTRITIONED && (
                            <div className="row">
                                <div className="col-12 col-md-6 mb-8">
                                    <label className="form-label fw-bold required">Orientation End Date</label>
                                    <Calendar
                                        showIcon
                                        className="w-100"
                                        placeholder="Orientation End Date"
                                        value={orientationEndDate}
                                        onChange={(e) => setOrientationEndDate(normalizeDate(e.value))}
                                    />
                                </div>
                                <div className="col-12 col-md-6 mb-8">
                                    <label className="form-label required">Reason</label>
                                    <Dropdown
                                        value={selectedReasonId}
                                        options={reasons ?? []}
                                        optionLabel="name"
                                        optionValue="id"
                                        placeholder="Select an option"
                                        className="w-100"
                                        onChange={(e) => setSelectedReasonId(e.value)}
                                    />
                                </div>
                            </div>
                        )}

                        {selectedStatusId === ORIENTATION_STATUS.COMPLETED && (
                            <div className="row">
                                <div className="col-12 col-md-6 mb-8">
                                    <label className="form-label fw-bold required">Orientation End Date</label>
                                    <Calendar
                                        showIcon
                                        className="w-100 p-dropdown-tall"
                                        placeholder="Orientation End Date"
                                        value={orientationEndDate}
                                        onChange={(e) => setOrientationEndDate(normalizeDate(e.value))}
                                    />
                                </div>
                                <div className="col-12 col-md-6 mb-8">
                                    <label className="form-label fw-bold required">
                                        Number of precepted shifts in final week
                                    </label>
                                    <input
                                        type="number"
                                        min={0}
                                        className="form-control form-control-solid"
                                        value={preceptedShiftsInFinalWeek}
                                        onChange={(e) =>
                                            setPreceptedShiftsInFinalWeek(
                                                e.target.value === "" ? null : Number(e.target.value)
                                            )
                                        }
                                    />
                                </div>
                                <div className="col-12 col-md-6 mb-8">
                                    <label className="form-label required">Convert User to Preceptor</label>
                                    <div className="d-flex flex-row flex-center flex-md-stack gap-2">
                                        <div className="flex-fill">
                                            <input
                                                id="id-make-preceptor-no"
                                                type="radio"
                                                className="btn-check"
                                                name="make_preceptor"
                                                value={false}
                                                checked={!makePreceptor}
                                                onChange={() => setMakePreceptor(false)}
                                            />
                                            <label
                                                className={cx(
                                                    "btn btn-outline btn-active-primary btn-flex flex-center w-100",
                                                    {
                                                        active: !makePreceptor,
                                                    }
                                                )}
                                                htmlFor="id-make-preceptor-no"
                                            >
                                                No
                                            </label>
                                        </div>
                                        <div className="flex-fill">
                                            <input
                                                id="id-make-preceptor-yes"
                                                type="radio"
                                                className="btn-check"
                                                name="make_preceptor"
                                                value={true}
                                                checked={makePreceptor}
                                                onChange={() => setMakePreceptor(true)}
                                            />
                                            <label
                                                className={cx(
                                                    "btn btn-outline btn-active-primary btn-flex flex-center w-100",
                                                    {
                                                        active: makePreceptor,
                                                    }
                                                )}
                                                htmlFor="id-make-preceptor-yes"
                                            >
                                                Yes
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {selectedStatusId === ORIENTATION_STATUS.TRANSFER && (
                            <div>
                                <div className="d-flex flex-row h3">Transfer Orientee</div>
                                <div className="h4 my-5">New Location</div>
                                <div className="row">
                                    <div className="col-12 col-md-6 mb-8">
                                        <label className="form-label fw-bold">Organization</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-solid"
                                            value={orientation?.organization_name}
                                            readOnly
                                        />
                                    </div>
                                    <div className="col-12 col-md-6 mb-8">
                                        <label className="form-label fw-bold">Facility</label>
                                        <Dropdown
                                            className="w-100 p-dropdown-tall"
                                            panelClassName="show-10"
                                            showClear={true}
                                            name="facility_id"
                                            value={facilityId}
                                            options={facilities.map((org) => {
                                                return {label: org.name, value: org.id}
                                            })}
                                            filter={false}
                                            onChange={(evt) => {
                                                setFacilityId(evt.target.value)
                                                fetchUnits(evt.target.value)
                                            }}
                                        />
                                    </div>
                                    <div className="col-12 col-md-6 mb-8">
                                        <label className="form-label fw-bold">Unit</label>

                                        {isUnitLoading && <Skeleton width="100%" height="3rem" />}

                                        {!isUnitLoading && (
                                            <Dropdown
                                                className="w-100 p-dropdown-tall"
                                                panelClassName="show-10"
                                                showClear={true}
                                                name="unit_id"
                                                value={unitId}
                                                options={units.map((org) => {
                                                    return {label: org.name, value: org.id}
                                                })}
                                                filter={false}
                                                onChange={(evt) => {
                                                    setUnitId(evt.target.value)
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className="col-12 col-md-6 mb-8">
                                        <label className="form-label fw-bold">Continue Orientation</label>

                                        <Dropdown
                                            className="w-100 p-dropdown-tall"
                                            panelClassName="show-10"
                                            showClear={true}
                                            name="continue_orientation"
                                            value={continueOrientation}
                                            options={[
                                                {label: "Continue Current Orientation on New Unit", value: "current"},
                                                {label: "Start New Orientation on New Unit", value: "new"},
                                            ]}
                                            filter={false}
                                            onChange={(evt) => {
                                                setContinueOrientation(evt.target.value)
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {selectedStatusId === ORIENTATION_STATUS.INELIGIBLE && (
                            <div className="row">
                                <div className="col-12 col-md-6 mb-8">
                                    <label className="form-label required">Reason</label>
                                    <Dropdown
                                        value={ineligibleReasonId}
                                        options={ineligibleReasons ?? []}
                                        optionLabel="name"
                                        optionValue="id"
                                        placeholder="Select an option"
                                        className="w-100"
                                        onChange={(e) => setIneligibleReasonId(e.value)}
                                    />
                                </div>
                            </div>
                        )}

                        {hasChange && (
                            <div className="row justify-content-end">
                                {isResumingFromPause && (
                                    <div className="col-12 mb-3">
                                        <div className="alert alert-info mb-0">
                                            Resuming today will add{" "}
                                            {getWeeksToAddPreview(orientation.suspension_start_date)} week(s) to this
                                            orientee's total paused weeks (currently {orientation.total_paused_weeks ?? 0}).
                                        </div>
                                    </div>
                                )}
                                <div className="col-12 col-md-6 mb-3 text-end">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        disabled={saveDisabled}
                                        onClick={handleSave}
                                    >
                                        {saving ? "Saving..." : "Save"}
                                    </button>
                                </div>
                            </div>
                        )}

                        {error && <div className="alert alert-danger">{error}</div>}
                    </div>
                </div>
            </div>
        </>
    )
}

export default EducatorOrienteeStatus
